import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./style.css";
import "./mdm-login.css";
import styled from "styled-components";
import {
  FiUser,
  FiLock,
  FiEye,
  FiEyeOff,
  FiBriefcase,
  FiChevronDown,
  FiGlobe,
  FiKey,
  FiHeadphones,
  FiCalendar,
  FiMail,
  FiPhone,
} from "react-icons/fi";
import { AppContext, useApp } from "../../AppContext";
import { setSessionFunc } from "../../api/func/userFunc";
import loginVisual from "../../images/login-left-visual.png";

function CustomSelect({ id, value, options, onChange, icon: Icon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="mdm-input mdm-input--select-custom" id={id}>
      {Icon && <Icon className="mdm-input__icon" />}
      <button
        type="button"
        className="mdm-select-button"
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? selected.label : <span className="mdm-select-placeholder">선택</span>}
      </button>
      <FiChevronDown
        className="mdm-input__chevron"
        style={{ transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)" }}
      />
      {open && (
        <ul className="mdm-select-dropdown" role="listbox">
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
              className={`mdm-select-option${value === opt.value ? " is-active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const DOMAIN_OPTIONS = [{ value: "antsnest", label: "앤츠네스트" }];

const Input = styled.input`
  /* 기본 스타일 */
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;

  /* 자동 완성 색상 및 스타일 없애기 */
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important; /* 자동 완성 배경색 제거 */
    box-shadow: 0 0 0px 1000px transparent inset !important;
    -webkit-text-fill-color: white !important; /* 텍스트 색상 유지 */
    transition: background-color 5000s ease-in-out 0s;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-transition: background-color 9999s ease-out;
    -webkit-text-fill-color: white !important; /* 텍스트 색상 유지 */
  }
`;
function RegisterForm() {
  const [username, setUsername] = useState("");
  const [registerId, setRegisterId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [gender, setGender] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [domain, setDomain] = useState("antsnest");
  const navigate = useNavigate();

  const years = [];
  for (let y = 2026; y >= 1930; y--) years.push(y);

  const validateForm = () => {
    if (registerId.length > 0 && username.length > 0 && password.length > 0) {
      if (password !== confirmPassword) {
        setMessage("비밀번호가 일치하지 않습니다.");
        return false;
      }
      return true;
    } else {
      setMessage("필수 항목(아이디/이름/비밀번호)을 입력해주세요.");
      return false;
    }
  };

  const handleDomainChange = (e) => setDomain(e.target.value);

  const handleSubmitRegister = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      try {
        const response = await fetch("/request/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: registerId,
            user_name: username,
            user_pwd: password,
            gender: gender,
            birth_y: birthYear,
            birth_m: birthMonth,
            birth_d: birthDay,
            email: email,
            phone: phone,
            alias: "",
            domain: domain,
          }),
        });

        if (!response.ok) throw new Error(response.statusText);
        await response.text();
        navigate("/login");
      } catch (error) {
        console.error("회원가입 실패:", error);
        setMessage("회원가입에 실패하였습니다.");
      }
    }
  };

  return (
    <div className="mdm-login-page">
      <div className="mdm-login-card mdm-login-card--register">
        <aside className="mdm-login-visual">
          <img
            src={loginVisual}
            alt="MDM 기준정보 관리 시스템"
            className="mdm-login-visual__img"
          />
        </aside>

        <section className="mdm-login-form mdm-login-form--register">
          <div className="mdm-login-form__body">
            <h1 className="mdm-login-form__title">회원가입</h1>
            <p className="mdm-login-form__subtitle">
              계정을 생성하고 앤츠네스트의 다양한 서비스를 이용해보세요.
            </p>
            <div className="mdm-login-form__header-divider" />

            {message && <p className="mdm-login-form__error">{message}</p>}

            <form autoComplete="off" onSubmit={handleSubmitRegister}>
              <div className="mdm-field">
                <label className="mdm-field__label">회사/조직</label>
                <CustomSelect
                  id="reg-domain"
                  value={domain}
                  options={DOMAIN_OPTIONS}
                  onChange={setDomain}
                  icon={FiBriefcase}
                />
              </div>

              <div className="mdm-form-row">
                <div className="mdm-field">
                  <label htmlFor="reg-id" className="mdm-field__label">
                    아이디
                  </label>
                  <div className="mdm-input">
                    <FiUser className="mdm-input__icon" />
                    <input
                      id="reg-id"
                      type="text"
                      className="mdm-input__field"
                      placeholder="아이디를 입력하세요"
                      value={registerId}
                      onChange={(e) => setRegisterId(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
                <div className="mdm-field">
                  <label htmlFor="reg-name" className="mdm-field__label">
                    이름
                  </label>
                  <div className="mdm-input">
                    <FiUser className="mdm-input__icon" />
                    <input
                      id="reg-name"
                      type="text"
                      className="mdm-input__field"
                      placeholder="이름을 입력하세요"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mdm-form-row">
                <div className="mdm-field">
                  <label htmlFor="reg-pw" className="mdm-field__label">
                    비밀번호
                  </label>
                  <div className="mdm-input">
                    <FiLock className="mdm-input__icon" />
                    <input
                      id="reg-pw"
                      type={showPassword ? "text" : "password"}
                      className="mdm-input__field"
                      placeholder="비밀번호를 입력하세요"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="mdm-input__toggle"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="mdm-field">
                  <label htmlFor="reg-pw2" className="mdm-field__label">
                    비밀번호 확인
                  </label>
                  <div className="mdm-input">
                    <FiLock className="mdm-input__icon" />
                    <input
                      id="reg-pw2"
                      type={showConfirmPassword ? "text" : "password"}
                      className="mdm-input__field"
                      placeholder="비밀번호를 다시 입력하세요"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="mdm-input__toggle"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      aria-label={showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
                    >
                      {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mdm-field">
                <span className="mdm-field__label">성별</span>
                <div className="mdm-radio-group">
                  <label className="mdm-radio">
                    <input
                      type="radio"
                      name="gender"
                      value="1"
                      checked={gender === "1"}
                      onChange={(e) => setGender(e.target.value)}
                    />
                    <span>남성</span>
                  </label>
                  <label className="mdm-radio">
                    <input
                      type="radio"
                      name="gender"
                      value="2"
                      checked={gender === "2"}
                      onChange={(e) => setGender(e.target.value)}
                    />
                    <span>여성</span>
                  </label>
                </div>
              </div>

              <div className="mdm-field">
                <span className="mdm-field__label">생년월일</span>
                <div className="mdm-form-row mdm-form-row--three" style={{ marginBottom: 0 }}>
                  <div className="mdm-input mdm-input--select">
                    <FiCalendar className="mdm-input__icon" />
                    <select
                      className="mdm-input__field"
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                    >
                      <option value="">연도 선택</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}년
                        </option>
                      ))}
                    </select>
                    <FiChevronDown className="mdm-input__chevron" />
                  </div>
                  <div className="mdm-input mdm-input--select">
                    <select
                      className="mdm-input__field"
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      style={{ paddingLeft: 14 }}
                    >
                      <option value="">월 선택</option>
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map(
                        (m) => (
                          <option key={m} value={m}>
                            {parseInt(m, 10)}월
                          </option>
                        )
                      )}
                    </select>
                    <FiChevronDown className="mdm-input__chevron" />
                  </div>
                  <div className="mdm-input mdm-input--select">
                    <select
                      className="mdm-input__field"
                      value={birthDay}
                      onChange={(e) => setBirthDay(e.target.value)}
                      style={{ paddingLeft: 14 }}
                    >
                      <option value="">일 선택</option>
                      {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map(
                        (d) => (
                          <option key={d} value={d}>
                            {parseInt(d, 10)}일
                          </option>
                        )
                      )}
                    </select>
                    <FiChevronDown className="mdm-input__chevron" />
                  </div>
                </div>
              </div>

              <div className="mdm-form-row">
                <div className="mdm-field">
                  <label htmlFor="reg-email" className="mdm-field__label">
                    이메일
                  </label>
                  <div className="mdm-input">
                    <FiMail className="mdm-input__icon" />
                    <input
                      id="reg-email"
                      type="email"
                      className="mdm-input__field"
                      placeholder="이메일을 입력하세요"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mdm-field">
                  <label htmlFor="reg-phone" className="mdm-field__label">
                    전화번호
                  </label>
                  <div className="mdm-input">
                    <FiPhone className="mdm-input__icon" />
                    <input
                      id="reg-phone"
                      type="tel"
                      className="mdm-input__field"
                      placeholder="전화번호를 입력하세요"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="mdm-btn mdm-btn--primary" style={{ marginTop: 8 }}>
                회원가입
              </button>

              <p className="mdm-login-signup">
                이미 계정이 있으신가요?{" "}
                <Link to="/login" className="mdm-login-signup__link">
                  로그인
                </Link>
              </p>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function Login() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [domain, setDomain] = useState("antsnest");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberId, setRememberId] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { login } = useApp();

  useEffect(() => {
    const saved = localStorage.getItem("savedUserId");
    if (saved) {
      setUserId(saved);
      setRememberId(true);
    }
  }, []);

  const handleDomainChange = (e) => {
    setDomain(e.target.value);
  };

  const validateForm = () => {
    if (!domain) {
      setMessage("회사/조직을 선택해주세요.");
      return false;
    }
    if (!userId) {
      setMessage("아이디를 입력해주세요.");
      return false;
    }
    if (!password) {
      setMessage("비밀번호를 입력해주세요.");
      return false;
    }
    setMessage("");
    return true;
  };

  const handleLoginClick = async () => {
    if (validateForm()) {
      try {
        const response = await fetch("/request/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            user_pwd: password,
            domain: domain,
          }),
        });

        if (response.ok) {
          const userData = await response.json();
          login(userData);

          if (rememberId) {
            localStorage.setItem("savedUserId", userId);
          } else {
            localStorage.removeItem("savedUserId");
          }

          const setSession = await setSessionFunc(userData);
          const redirectTo = location.state?.from || "/";
          if (setSession.code == 200) {
            navigate(redirectTo);
          } else {
            setMessage("서버 확인이 필요합니다.");
          }
        } else {
          const errorText = await response.text();
          setMessage(errorText);
        }
      } catch (error) {
        console.error("로그인 오류:", error);
        setMessage("로그인 요청 중 문제가 발생했습니다.");
      }
      setPassword("");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLoginClick();
  };

  return (
    <div className="mdm-login-page">
      <div className="mdm-login-card">
        <aside className="mdm-login-visual">
          <img
            src={loginVisual}
            alt="MDM 기준정보 관리 시스템"
            className="mdm-login-visual__img"
          />
        </aside>

        <section className="mdm-login-form">
          <div className="mdm-login-form__lang">
            <button type="button" className="mdm-lang-btn">
              <FiGlobe size={14} />
              <span>한국어</span>
              <FiChevronDown size={14} />
            </button>
          </div>

          <div className="mdm-login-form__body">
            <h1 className="mdm-login-form__title">시스템에 로그인</h1>
            <p className="mdm-login-form__subtitle">
              계정 정보를 입력하여 로그인하세요.
            </p>

            {message && <p className="mdm-login-form__error">{message}</p>}

            <form autoComplete="off" onSubmit={handleSubmit}>
              <div className="mdm-field">
                <label htmlFor="mdm-login-id" className="mdm-field__label">
                  아이디
                </label>
                <div className="mdm-input">
                  <FiUser className="mdm-input__icon" />
                  <input
                    id="mdm-login-id"
                    type="text"
                    name="id"
                    className="mdm-input__field"
                    placeholder="아이디를 입력하세요"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="mdm-field">
                <label htmlFor="mdm-login-pw" className="mdm-field__label">
                  비밀번호
                </label>
                <div className="mdm-input">
                  <FiLock className="mdm-input__icon" />
                  <input
                    id="mdm-login-pw"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="mdm-input__field"
                    placeholder="비밀번호를 입력하세요"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="mdm-input__toggle"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              <div className="mdm-field">
                <label className="mdm-field__label">회사/조직</label>
                <CustomSelect
                  id="mdm-login-domain"
                  value={domain}
                  options={DOMAIN_OPTIONS}
                  onChange={setDomain}
                  icon={FiBriefcase}
                />
              </div>

              <div className="mdm-login-options">
                <label className="mdm-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberId}
                    onChange={(e) => setRememberId(e.target.checked)}
                  />
                  <span>아이디 저장</span>
                </label>
                <button type="button" className="mdm-login-options__link">
                  비밀번호 찾기
                </button>
              </div>

              <button type="submit" className="mdm-btn mdm-btn--primary">
                로그인
              </button>

              <div className="mdm-divider">
                <span>또는</span>
              </div>

              <button type="button" className="mdm-btn mdm-btn--outline">
                <FiKey size={16} />
                <span>SSO로 로그인</span>
              </button>

              <p className="mdm-login-signup">
                계정이 없으신가요?{" "}
                <Link to="/register" className="mdm-login-signup__link">
                  회원가입
                </Link>
              </p>
            </form>
          </div>

          <footer className="mdm-login-form__footer">
            <FiHeadphones className="mdm-login-form__footer-icon" />
            <span>로그인 관련 문의</span>
            <span>02-1234-5678</span>
            <span className="mdm-login-form__footer-divider">|</span>
            <span>help@antsnest.com</span>
          </footer>
        </section>
      </div>
    </div>
  );
}

export { RegisterForm, Login };
